import type { AparenciaEstado, EstadoIncidente, EstadoServico } from '@/types/status.types';

/** Onde o Nicon publica o snapshot. Sobrescrevível por env no build. */
export const URL_STATUS = import.meta.env.VITE_URL_STATUS || '/status.json';

export const INTERVALO_ATUALIZACAO_MS = 30_000;

/** Timeout do fetch. Curto de propósito: rede ruim não pode travar a página. */
export const TIMEOUT_FETCH_MS = 10_000;

/** De quanto em quanto tempo a idade do snapshot é recalculada na tela. */
export const INTERVALO_RELOGIO_MS = 15_000;

/**
 * Limites de frescor do snapshot.
 *
 * Acima de OBSOLETO a página para de afirmar qualquer coisa sobre a saúde dos
 * serviços e passa a declarar "desconhecido". Um publisher parado é indistinguível
 * de um sistema saudável do lado do cliente, e mostrar verde nesse caso é pior do
 * que admitir que não se sabe.
 */
export const IDADE_ATRASADO_SEGUNDOS = 5 * 60;
export const IDADE_OBSOLETO_SEGUNDOS = 15 * 60;

/** Cache local do último snapshot bom, para render imediato no reload. */
export const CHAVE_CACHE_SNAPSHOT = 'nicon-status:ultimo-snapshot';

export const APARENCIA_ESTADO_SERVICO: Record<EstadoServico, AparenciaEstado> = {
  operacional: { rotulo: 'Operacional', cor: 'var(--success-active)' },
  degradado: { rotulo: 'Desempenho degradado', cor: 'var(--warning-active)' },
  indisponivel: { rotulo: 'Indisponível', cor: 'var(--danger-active)' },
  manutencao: { rotulo: 'Em manutenção', cor: 'var(--info-active)' },
  desconhecido: { rotulo: 'Estado desconhecido', cor: 'var(--muted-foreground)' },
};

export const APARENCIA_ESTADO_INCIDENTE: Record<EstadoIncidente, AparenciaEstado> = {
  investigando: { rotulo: 'Investigando', cor: 'var(--danger-active)' },
  identificado: { rotulo: 'Identificado', cor: 'var(--warning-active)' },
  monitorando: { rotulo: 'Monitorando', cor: 'var(--info-active)' },
  resolvido: { rotulo: 'Resolvido', cor: 'var(--success-active)' },
};

/** Cor de cada quadradinho da barra de disponibilidade. */
export const COR_DIA_DISPONIBILIDADE: Record<EstadoServico, string> = {
  operacional: 'color-mix(in oklch, var(--success) 80%, var(--card))',
  degradado: 'var(--warning)',
  indisponivel: 'var(--danger)',
  manutencao: 'color-mix(in oklch, var(--info) 55%, var(--card))',
  desconhecido: 'color-mix(in oklch, var(--muted-foreground) 30%, var(--card))',
};

export const DESCRICAO_DIA_DISPONIBILIDADE: Record<EstadoServico, string> = {
  operacional: 'Sem interrupções',
  degradado: 'Desempenho degradado',
  indisponivel: 'Indisponibilidade parcial',
  manutencao: 'Manutenção programada',
  desconhecido: 'Sem dados',
};

/** Do mais saudável ao mais grave. Usado para derivar o estado geral. */
export const SEVERIDADE_ESTADO: Record<EstadoServico, number> = {
  operacional: 0,
  manutencao: 1,
  desconhecido: 2,
  degradado: 3,
  indisponivel: 4,
};

export const TITULO_ESTADO_GERAL: Record<EstadoServico, string> = {
  operacional: 'Todos os serviços operacionais',
  degradado: 'Alguns serviços com desempenho degradado',
  indisponivel: 'Há serviços indisponíveis',
  manutencao: 'Manutenção em andamento',
  desconhecido: 'Não foi possível confirmar o estado dos serviços',
};

export const SUBTITULO_ESTADO_GERAL: Record<EstadoServico, string> = {
  operacional:
    'Nenhum incidente em andamento. Esta página é atualizada automaticamente e mostra o estado dos serviços que você usa.',
  degradado:
    'Parte dos serviços está funcionando com lentidão ou falhas intermitentes. Nossa equipe já está atuando.',
  indisponivel:
    'Um ou mais serviços estão indisponíveis. Nossa equipe já foi acionada e está trabalhando para restabelecer o funcionamento.',
  manutencao:
    'Estamos em uma janela de manutenção programada. Alguns recursos podem ficar temporariamente indisponíveis.',
  desconhecido:
    'Não recebemos atualizações recentes do monitoramento, então não podemos confirmar o estado dos serviços agora. Se você está com dificuldades, entre em contato com o suporte.',
};

export const COR_PONTO_ESTADO_GERAL: Record<EstadoServico, string> = {
  operacional: 'var(--success)',
  degradado: 'var(--warning)',
  indisponivel: 'var(--danger)',
  manutencao: 'var(--info)',
  desconhecido: 'var(--muted-foreground)',
};
